import { useAuthStore } from 'lib/stores/authStore';
import { hasCachedOrdersForEvent, refreshOrdersForEvent, useUserOrdersStore } from 'lib/stores/userOrdersStore';
import { refreshEventPosition } from 'queries/eventPosition';
import { resolveEventTickerForMarketId } from 'stores/zustand/marketEntitiesStore';
import { WS_CLOSE_CODE_INACTIVITY_RECYCLE } from 'transport/ws';
import { legacyExchangeWsClient } from 'transport/wsClients';

import { useAppNavigationStore } from './zustand/appNavigationStore';
import { useSubAccountStore } from './zustand/subAccountStore';
import { tradingContextSelectors, useTradingContextStore } from './zustand/tradingContextStore';

// Reference-counted against `useTradingSync`'s single mount effect (guards
// StrictMode's double-invoke) and against the app effects `stop()` teardown,
// which also calls `stopTradingSync()` on unmount.
let startedCount = 0;
let unsubscribeTradingContext: (() => void) | null = null;
let unsubscribePathname: (() => void) | null = null;
let lastSyncedPathname: string | null = null;
let lastSyncedContextKey: string | null = null;
let unsubscribeConnection: (() => void) | null = null;
let lastConnectionStateConnected: boolean | null = null;
let visibilityHandler: (() => void) | null = null;
let unsubscribeAuth: (() => void) | null = null;
let unsubscribeSubaccount: (() => void) | null = null;
// Event tickers currently active AND confirmed fresh — WS (`user_orders`) is
// trusted to keep them correct on its own, so a ticker in this set isn't
// refetched on later re-navigation or visibility events. It's pruned the
// moment a ticker drops out of the active order-context set (see below —
// userOrdersStore's WS drain only applies `user_order` messages while a
// ticker's context is active, so anything that happened
// while it was inactive must be treated as unknown), and cleared entirely on
// a stale WS reconnect (a dead socket window can silently drop messages for
// every currently-active ticker, not just the ones that briefly deactivated).
let trustedOrderEventTickers = new Set<string>();
// Event tickers that have had at least one successful resting-orders fetch
// this session, ever — unlike `trustedOrderEventTickers`, this is NOT pruned
// when a ticker deactivates or the socket reconnects, only on logout/
// subaccount switch (when the underlying orders Map itself gets wiped, so
// there's nothing left to reconcile). It decides whether a fetch can skip
// reconciliation (nothing cached locally yet, so there's nothing stale to
// clean up) or must reconcile (a previously-resting order still cached
// locally may have since been filled/canceled while unwatched; a plain
// resting-only refetch can only add/update rows, so the stale row must be
// detected by diffing the response against the cached resting set — see
// userOrdersStore's `refreshOrdersForEvent`).
let seededOrderEventTickers = new Set<string>();
// Bumped on every event that can make an in-flight order fetch's eventual
// response untrustworthy: a stale WS disconnect (`attachConnectionListener`),
// a subaccount switch, or a logout (both below) — each wipes/rescopes the
// underlying `userOrdersStore` cache out from under any request already in
// flight. That request can still succeed afterward, but its response was
// requested before the gap/switch and can't reflect anything that changed
// (or belongs to a different subaccount) since — so its completion handler
// compares against the CURRENT epoch, not the one it captured at request
// time, to know whether to trust that response or redo it instead.
let orderDataEpoch = 0;

function attemptRefreshOrdersForEvent(eventTicker: string, reconcile: boolean): void {
    const epochAtRequest = orderDataEpoch;
    // Only mark as trusted/seeded once the request actually succeeds — a
    // failed attempt (or one skipped because another was already in flight)
    // must not be treated as done, or it would never retry.
    refreshOrdersForEvent(eventTicker, resolveEventTickerForMarketId, {
        reconcile,
        // Checked right before the response is written into the shared
        // orders cache — a logout/subaccount switch/stale reconnect that
        // happens while this request is in flight must not let its (now
        // out-of-date) response repopulate the cache, even though the epoch
        // re-check in `onSettled` below still runs to decide whether to redo
        // the fetch.
        isStale: () => orderDataEpoch !== epochAtRequest,
        onSettled: (succeeded: boolean) => {
            if (!succeeded) {
                return;
            }
            // The response did populate real data, so any later refetch for
            // this ticker must reconcile against it — hence marking it
            // seeded unconditionally.
            seededOrderEventTickers.add(eventTicker);

            // This callback can resolve after the user has already navigated
            // away, so re-check activeness at completion time (not the stale
            // snapshot the caller captured) before trusting it — otherwise a
            // late response would let a since-reactivated ticker skip the
            // reconciliation fetch it still needs.
            const isStillActive = tradingContextSelectors
                .activeOrderEventTickers(useTradingContextStore.getState())
                .includes(eventTicker);
            if (!isStillActive) {
                return;
            }

            if (orderDataEpoch !== epochAtRequest) {
                // A stale disconnect, subaccount switch, or logout happened
                // while this fetch was already in flight (so that trigger's
                // own resync attempt was itself skipped as a duplicate
                // in-flight request, or never fired one at all). This
                // response was requested before the gap and can't be trusted
                // — redo it as a reconciliation fetch instead of marking the
                // ticker trusted.
                attemptRefreshOrdersForEvent(eventTicker, true);
                return;
            }

            trustedOrderEventTickers.add(eventTicker);
        },
    });
}

function syncTradingDataForContext(): void {
    const { isLoggedInMember } = useAuthStore.getState();
    if (!isLoggedInMember) {
        return;
    }

    const tradingContextState = useTradingContextStore.getState();

    tradingContextSelectors.activeEventTickers(tradingContextState).forEach((eventTicker) => {
        refreshEventPosition(eventTicker);
    });

    const activeOrderEventTickers = tradingContextSelectors.activeOrderEventTickers(tradingContextState);
    const activeOrderEventTickerSet = new Set(activeOrderEventTickers);
    for (const ticker of trustedOrderEventTickers) {
        if (!activeOrderEventTickerSet.has(ticker)) {
            trustedOrderEventTickers.delete(ticker);
        }
    }

    activeOrderEventTickers.forEach((eventTicker) => {
        if (!trustedOrderEventTickers.has(eventTicker)) {
            // A market-scoped load (e.g. a market-scoped resting fetch from
            // the resting-orders modal) can have already written orders
            // for this event into the shared cache without ever going
            // through `attemptRefreshOrdersForEvent` — `seededOrderEventTickers`
            // alone wouldn't know that, and would wrongly treat this as a
            // true first-ever fetch with nothing cached to reconcile.
            const isFirstFetchThisSession =
                !seededOrderEventTickers.has(eventTicker) &&
                !hasCachedOrdersForEvent(
                    useUserOrdersStore.getState().orders,
                    eventTicker,
                    resolveEventTickerForMarketId,
                );
            attemptRefreshOrdersForEvent(eventTicker, !isFirstFetchThisSession);
        }
    });
}

function hasActiveContext(): boolean {
    const tradingContextState = useTradingContextStore.getState();
    return (
        tradingContextSelectors.portfolioActive(tradingContextState) ||
        tradingContextSelectors.activeEventTickers(tradingContextState).length > 0 ||
        tradingContextSelectors.activeOrderEventTickers(tradingContextState).length > 0
    );
}

function trySyncForNavigation(): void {
    const pathname = useAppNavigationStore.getState().currentPathname;
    if (!pathname) {
        return;
    }
    if (!hasActiveContext()) {
        // No trading context is registered at all right now (e.g. the user
        // navigated to a route that mounts none). `syncTradingDataForContext`
        // — the only place that prunes deactivated tickers out of
        // `trustedOrderEventTickers` — never runs in that case, so do it here
        // instead: every currently-trusted ticker just went inactive, and if
        // one of them reactivates later while still (wrongly) marked
        // trusted, it would skip the reconciliation fetch it needs. Also
        // clear the dedupe snapshot below: if the context comes back with the
        // exact same ticker set as before this gap, `contextKey` would
        // otherwise match the stale recorded value and skip the resync
        // entirely, silently keeping the (correctly) cleared trust from ever
        // being acted on.
        trustedOrderEventTickers = new Set();
        lastSyncedPathname = null;
        lastSyncedContextKey = null;
        return;
    }
    const contextKey = tradingContextSelectors.contextKey(useTradingContextStore.getState());
    if (lastSyncedPathname === pathname && lastSyncedContextKey === contextKey) {
        return;
    }
    lastSyncedPathname = pathname;
    lastSyncedContextKey = contextKey;
    syncTradingDataForContext();
}

function ensureWsConnected(): void {
    const { isLoggedInMember } = useAuthStore.getState();
    if (!isLoggedInMember) {
        return;
    }
    if (!legacyExchangeWsClient.isWsConnected()) {
        legacyExchangeWsClient.connect();
    }
}

function attachTradingContextSubscription(): void {
    if (unsubscribeTradingContext) {
        return;
    }
    unsubscribeTradingContext = useTradingContextStore.subscribe(() => trySyncForNavigation());
}

function detachTradingContextSubscription(): void {
    if (unsubscribeTradingContext) {
        unsubscribeTradingContext();
        unsubscribeTradingContext = null;
    }
}

function attachPathnameSubscription(): void {
    if (unsubscribePathname) {
        return;
    }
    unsubscribePathname = useAppNavigationStore.subscribe(() => trySyncForNavigation());
}

function detachPathnameSubscription(): void {
    if (unsubscribePathname) {
        unsubscribePathname();
        unsubscribePathname = null;
    }
}

function attachVisibilityListener(): void {
    if (visibilityHandler) {
        return;
    }
    visibilityHandler = (): void => {
        if (document.visibilityState === 'visible') {
            ensureWsConnected();
            syncTradingDataForContext();
        }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
}

function detachVisibilityListener(): void {
    if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
    }
}

function attachConnectionListener(): void {
    if (unsubscribeConnection) {
        return;
    }
    unsubscribeConnection = legacyExchangeWsClient.subscribeConnectionState((state) => {
        const { isLoggedInMember } = useAuthStore.getState();
        if (!isLoggedInMember) {
            return;
        }
        if (state.connected) {
            // WsService owns stale-recovery telemetry; this listener only coordinates resyncs.
            lastConnectionStateConnected = true;
            return;
        }
        // The socket intentionally rotates after 60s without a message, and the reconnect
        // resubscribes cold (no snapshot replay) — an order/fill/position update that arrived
        // right as the old socket went silently dead (the exact case a 60s-idle rotation can't
        // distinguish from a truly idle period) would otherwise be lost forever. Only skip the
        // resync here when nothing is actively displayed/tracked (nothing to protect); any
        // active context still needs the same resync a real disconnect gets below.
        if (state.code === WS_CLOSE_CODE_INACTIVITY_RECYCLE && !hasActiveContext()) {
            return;
        }
        if (lastConnectionStateConnected === false) {
            return;
        }
        lastConnectionStateConnected = false;
        ensureWsConnected();
        // The reconnect always resubscribes cold (no snapshot replay), so
        // `user_order` messages during the socket's dead time are silently
        // missed — no currently-active ticker can be trusted anymore. Clear
        // only the trust set (not `seededOrderEventTickers`): the forced
        // refetch below must still be treated as a reconciliation fetch (so a
        // cached resting order that quietly filled/canceled during the gap
        // gets dropped), not a fresh first-ever fetch. Bumping the
        // epoch also catches a fetch that was already in flight when the gap
        // started (see `attemptRefreshOrdersForEvent`) — its own resync
        // attempt below is a no-op duplicate while that fetch is in flight,
        // so without the epoch check its eventual success would otherwise
        // grant trust based on a response requested before the gap.
        trustedOrderEventTickers = new Set();
        orderDataEpoch += 1;
        if (hasActiveContext()) {
            syncTradingDataForContext();
        }
    });
}

function detachConnectionListener(): void {
    if (unsubscribeConnection) {
        unsubscribeConnection();
        unsubscribeConnection = null;
    }
}

function attachAuthSubscription(): void {
    if (unsubscribeAuth) {
        return;
    }
    // `UserStore.endSession()`/`applyForcedDisconnectTeardown()` clear the
    // per-event order-loading state, but that only runs `resetAppStateOnLogout()`
    // — `stopTradingSync()` only fires on unmount teardown (see the app effects
    // `stop()`), so this module's own tracking would otherwise survive a logout
    // that keeps `AppEffectsBootstrap` mounted.
    // Reset it here too, or a ticker that was active before logout would look
    // "still active" after a fresh login and never get its first refresh.
    unsubscribeAuth = useAuthStore.subscribe((state, prevState) => {
        if (state.isLoggedInMember === prevState.isLoggedInMember) {
            return;
        }
        if (!state.isLoggedInMember) {
            // The orders Map itself gets wiped on logout, so there's nothing
            // left to reconcile — the next fetch for any ticker can go back
            // to being a fresh, resting-only, first-ever fetch. Bumping the
            // epoch also stops a fetch already in flight before logout from
            // granting trust (or re-populating the Map) if it resolves late,
            // after a subsequent login.
            trustedOrderEventTickers = new Set();
            seededOrderEventTickers = new Set();
            orderDataEpoch += 1;
        }
    });
}

function detachAuthSubscription(): void {
    if (unsubscribeAuth) {
        unsubscribeAuth();
        unsubscribeAuth = null;
    }
}

function attachSubaccountSubscription(): void {
    if (unsubscribeSubaccount) {
        return;
    }
    // `userOrdersStore` resets itself on subaccount switch,
    // so there's nothing left to reconcile — same as logout, both sets reset.
    // Bumping the epoch also stops a fetch already in flight for the OLD
    // subaccount from granting trust for the new one if it resolves late.
    unsubscribeSubaccount = useSubAccountStore.subscribe((state, prevState) => {
        if (state.subaccountNumber === prevState.subaccountNumber) {
            return;
        }
        trustedOrderEventTickers = new Set();
        seededOrderEventTickers = new Set();
        orderDataEpoch += 1;
    });
}

function detachSubaccountSubscription(): void {
    if (unsubscribeSubaccount) {
        unsubscribeSubaccount();
        unsubscribeSubaccount = null;
    }
}

/**
 * Keeps live portfolio/order data in sync with whatever trading context is
 * currently active (`stores/zustand/tradingContextStore.ts`) — reconnecting
 * the WS on visibility/connection changes and refreshing positions/orders on
 * navigation. Reference-counted against `AppEffectsBootstrap`'s mount lifecycle
 * (see `app/components/AppEffectsBootstrap.tsx`) rather than owned by a
 * per-instance class, so it survives being started from more than one mount at once.
 */
export function startTradingSync(): void {
    startedCount += 1;
    if (startedCount > 1) {
        return;
    }
    if (typeof window === 'undefined') {
        return;
    }
    attachTradingContextSubscription();
    attachPathnameSubscription();
    attachVisibilityListener();
    attachConnectionListener();
    attachAuthSubscription();
    attachSubaccountSubscription();
    trySyncForNavigation();
}

export function stopTradingSync(): void {
    if (startedCount === 0) {
        return;
    }
    startedCount -= 1;
    if (startedCount > 0) {
        return;
    }
    detachTradingContextSubscription();
    detachVisibilityListener();
    detachPathnameSubscription();
    detachConnectionListener();
    detachAuthSubscription();
    detachSubaccountSubscription();
    lastSyncedPathname = null;
    lastSyncedContextKey = null;
    lastConnectionStateConnected = null;
    trustedOrderEventTickers = new Set();
    seededOrderEventTickers = new Set();
}
